import { Box, Typography, useTheme } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { HelpRequestMessageData } from '../model/types/submitHelpRequestSchema';

interface HelpRequestMessageProps {
    data: HelpRequestMessageData;
}
export const HelpRequestMessage = memo(({ data }: HelpRequestMessageProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    // Define visual styles for each message type using theme
    const getVariantStyles = () => {
        switch (data.type) {
            case 'success':
                return {
                    backgroundColor: theme.palette.success.light,
                    borderColor: theme.palette.success.light,
                    textColor: theme.palette.common.black,
                };
            case 'post-submission':
                return {
                    backgroundColor: theme.palette.warning.light,
                    borderColor: theme.palette.warning.main,
                    textColor: theme.palette.warning.dark,
                };
            default:
                return {
                    backgroundColor: theme.palette.grey[100],
                    borderColor: theme.palette.grey[300],
                    textColor: theme.palette.text.primary,
                };
        }
    };

    const styles = getVariantStyles();

    // Render content based on message type
    const renderContent = () => {
        switch (data.type) {
            case 'success':
                return (
                    <Typography component="span" variant="body2">
                        {t(
                            "Thanks! We've received your request and will get back to you " +
                                'within 24 hours (Mon–Fri, during business hours).',
                        )}
                    </Typography>
                );

            case 'post-submission': {
                return (
                    <>
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{ color: styles.textColor }}
                        >
                            {data.details?.requesterName}{' '}
                            {t('sent a help request on')}{' '}
                            <Typography
                                component="span"
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                            >
                                {data.details?.date}.
                            </Typography>
                        </Typography>
                        <br />
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{
                                color: styles.textColor,
                            }}
                        >
                            {t('Subject')}:{' '}
                            <Typography
                                component="span"
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                            >
                                {data.details?.issueType}
                            </Typography>
                        </Typography>
                    </>
                );
            }

            default:
                return null;
        }
    };

    return (
        <Box
            data-testid="help-request-message"
            sx={{
                backgroundColor: styles.backgroundColor,
                padding: '12px 20px',
                borderRadius: '4px',
                border: `1px solid ${styles.borderColor}`,
                mb: '24px',
            }}
        >
            {renderContent()}
        </Box>
    );
});

HelpRequestMessage.displayName = 'HelpRequestMessage';
